/**
 * ContributionBasisReportPage — the Contribution Basis generator
 * (PRD_ContributionBasisReport C1/C3/C6).
 *
 * Same shell as AttendanceSummaryReportPage: controls, Generate, then the
 * usePdfPreview + PdfPreviewPanel lifecycle — no on-screen table. Reads are the
 * 13th Month dialog's: the payroll index, then each candidate run's parent and
 * saved lines in parallel. A rejected read aborts the whole generate — never a
 * partial document.
 */
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { todayManilaYMD } from '../../utils/periodDate';
import { loadActiveLocks } from '../../services/dtrLockDerivation';
import { loadRunEmployees } from '../../services/payrollRunStore';
import {
  AGENCIES,
  selectCandidateRuns,
  defaultMonth,
  buildContributionBasisRows,
} from '../../services/contributionBasisReportModel';
import { longMonthLabel } from '../../constants/basisNotes';
import { loadContributionBasisPdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import PdfPreviewPanel from './PdfPreviewPanel';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--ui-border)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  background: 'var(--ui-surface)',
  fontFamily: 'var(--font-family-primary)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--ui-text-secondary)',
};

const cardStyle = {
  padding: '1.25rem',
  background: 'var(--ui-surface)',
  border: '1px solid var(--ui-border)',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const generatedStamp = (now) =>
  `${now.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' })} ${now.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Manila',
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const ContributionBasisReportPage = () => {
  const { company } = useAuth();
  const toast = useToast();

  // The month input's ceiling: the previous Manila month (only completed months).
  const maxMonth = defaultMonth([], todayManilaYMD());
  const [agency, setAgency] = useState('sss');
  const [month, setMonth] = useState(maxMonth);
  const [indexError, setIndexError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { preview, isMobile, showPreview, clearPreview } = usePdfPreview();

  // Open on the latest month that has data: a basis-on month only has rows once
  // a run starting the month after is approved.
  useEffect(() => {
    if (!company?.id) return;
    loadActiveLocks(company.id)
      .then((runs) => {
        setMonth(defaultMonth(runs, todayManilaYMD()));
        setIndexError(false);
      })
      .catch((err) => {
        console.error('[ContributionBasisReportPage] payroll index read failed:', err);
        setIndexError(true);
      });
  }, [company?.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let pdf;
      try {
        pdf = await loadContributionBasisPdf();
      } catch (err) {
        console.error('[ContributionBasisReportPage] PDF chunk load failed:', err);
        clearPreview();
        toast.error(PDF_LOAD_ERROR);
        return;
      }

      let model;
      try {
        const runs = selectCandidateRuns(await loadActiveLocks(company.id), month);
        const lineSets = await Promise.all(
          runs.map(async (r) => {
            const parentSnap = await getDoc(doc(db, 'companies', company.id, 'payrollRuns', r.runId));
            if (!parentSnap.exists()) throw new Error(`Payroll run ${r.runId} is missing.`);
            return [r.runId, await loadRunEmployees(company.id, r.runId, parentSnap.data())];
          })
        );
        model = buildContributionBasisRows({
          runs,
          linesByRunId: Object.fromEntries(lineSets),
          agency,
          month,
        });
      } catch (err) {
        console.error('[ContributionBasisReportPage] payroll read failed:', err);
        clearPreview();
        toast.error('Failed to load payroll runs. Please try again.');
        return;
      }

      const agencyLabel = AGENCIES[agency].label;
      if (model.rows.length === 0) {
        clearPreview();
        toast.error(`Nothing deducted or composed for ${agencyLabel} in ${longMonthLabel(month)} on an approved payroll run.`);
        return;
      }
      showPreview(
        pdf.buildContributionBasisPdf({
          companyName: company.name,
          agency,
          month,
          generatedAt: generatedStamp(new Date()),
          rows: model.rows,
          factorKeys: model.factorKeys,
        }),
        pdf.contributionBasisFileName({ agency, month }),
        `${agencyLabel} · ${longMonthLabel(month)}`
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateDisabled = generating || indexError || !month;

  return (
    <div>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ui-primary)', margin: '0 0 0.25rem 0' }}>
        Contribution Basis
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--ui-text-secondary)', margin: '0 0 1.5rem 0' }}>
        One month's SSS or PhilHealth employee share per employee — how the contribution was arrived
        at, what approved payroll runs deducted, and the difference. Not a government filing.
      </p>

      {indexError && (
        <div
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            background: 'var(--ui-danger-tint)',
            color: 'var(--ui-danger-text)',
            borderRadius: '8px',
            fontSize: '0.875rem',
          }}
        >
          Failed to load payroll runs. Reload the page and try again.
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
          <label style={labelStyle}>
            AGENCY
            <select value={agency} onChange={(e) => setAgency(e.target.value)} style={inputStyle}>
              <option value="sss">SSS</option>
              <option value="philhealth">PhilHealth</option>
            </select>
          </label>

          <label style={labelStyle}>
            MONTH
            <input
              type="month"
              value={month}
              max={maxMonth}
              onChange={(e) => setMonth(e.target.value)}
              style={inputStyle}
            />
          </label>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateDisabled}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--ui-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: generateDisabled ? 'not-allowed' : 'pointer',
              opacity: generateDisabled ? 0.6 : 1,
              fontFamily: 'var(--font-family-primary)',
            }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>

      <PdfPreviewPanel preview={preview} isMobile={isMobile} title="Contribution Basis preview" />
    </div>
  );
};

export default ContributionBasisReportPage;
